import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { CubeIcon, MapPinIcon, CalendarIcon, ClockIcon, PhoneIcon, UserIcon, ExclamationCircleIcon, CheckCircleIcon, TruckIcon, StarIcon } from '@heroicons/react/24/outline';
import { deviseCommande } from '../utils/devise';

const PublicOrderView = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // État pour vérifier si un feedback existe déjà
  const [feedbackExists, setFeedbackExists] = useState(false);

  // Statuts permettant de laisser un avis
  const FEEDBACK_ALLOWED_STATUSES = ['delivered', 'completed'];

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        // Appel à l'API publique sans authentification
        const response = await api.get(`/orders/public/${id}`);
        setOrder(response.data);

        // Vérifier si un feedback existe déjà pour cette commande
        if (FEEDBACK_ALLOWED_STATUSES.includes(response.data.status)) {
          try {
            const feedbackCheck = await api.get(`/order-feedback/public/${id}/check`);
            setFeedbackExists(feedbackCheck.data.exists);
          } catch (feedbackErr) {
            console.error('Erreur lors de la vérification du feedback:', feedbackErr);
          }
        }
      } catch (err) {
        console.error('Erreur lors de la récupération de la commande:', err);
        setError('Impossible de charger la commande. Vérifiez le lien ou réessayez plus tard.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrder();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600">{error || "Commande introuvable"}</p>
        </div>
      </div>
    );
  }

  // Helpers pour l'affichage
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      preparing: 'bg-yellow-100 text-yellow-800',
      ready_to_deliver: 'bg-purple-100 text-purple-800',
      in_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouvelle',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready_to_deliver: 'Prête pour livraison',
      in_delivery: 'En livraison',
      delivered: 'Livrée',
      cancelled: 'Annulée',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* En-tête avec Logo (placeholder) et Statut */}
        <div className="bg-white rounded-t-xl shadow-sm p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commande #{order.order_number}</h1>
            <p className="text-sm text-gray-500">
              Passée le {new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide ${getStatusColor(order.status)}`}>
            {getStatusLabel(order.status)}
          </span>
        </div>

        {/* Contenu Principal */}
        <div className="bg-white shadow-sm rounded-b-xl overflow-hidden">
          
          {/* Section Livraison */}
          <div className="p-6 border-b border-gray-100 bg-blue-50/50">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TruckIcon className="h-5 w-5 text-blue-600" /> Détails de livraison
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Client</p>
                    <p className="text-gray-600">{order.customer_name}</p>
                    {order.customer_phone && <p className="text-gray-500 text-sm">{order.customer_phone}</p>}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Adresse</p>
                    <p className="text-gray-600">{order.delivery_address}</p>
                    {order.delivery_notes && (
                      <p className="text-sm text-amber-600 mt-1 bg-amber-50 p-2 rounded">
                        Note de livraison: {order.delivery_notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Date prévue</p>
                    <p className="text-gray-600">
                      {order.delivery_date
                        ? new Date(order.delivery_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                        : 'Non définie'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Créneau</p>
                    <p className="text-gray-600">{order.delivery_time_slot || 'Standard'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes générales de la commande - Affichage conditionnel si notes disponibles */}
            {order.notes && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-gray-900 mb-1">Notes de la commande</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.notes}</p>
              </div>
            )}
          </div>

            {/* Liste des Articles */}
            <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CubeIcon className="h-5 w-5 text-blue-600" /> Articles ({order.items.length})
            </h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4 py-3 border-b border-gray-50 last:border-0">
                  {/* Image du produit */}
                  <div className="h-20 w-20 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden border border-gray-200">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.product_name} 
                        className="h-full w-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100?text=No+Image'; }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-gray-400">
                        <CubeIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900 line-clamp-1">{item.product_name}</h3>
                          {item.variant_title && (
                            <p className="text-sm text-gray-500 mt-0.5">{item.variant_title}</p>
                          )}
                          {item.notes && <p className="text-sm text-amber-600 mt-1 italic text-xs bg-amber-50 p-1 rounded inline-block">Note: "{item.notes}"</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            {(Number(item.product_price) * item.quantity).toLocaleString('fr-FR')} {deviseCommande(order)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {Number(item.product_price).toLocaleString('fr-FR')} x {item.quantity}
                          </p>
                        </div>
                      </div>
                      {item.is_bundle_item && <span className="mt-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full inline-block">Offre spéciale</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totaux */}
            <div className="mt-8 pt-6 border-t border-gray-100 bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2 text-gray-600">
                <span>Sous-total</span>
                <span>{(Number(order.total_amount) - Number(order.shipping_fee)).toLocaleString('fr-FR')} {deviseCommande(order)}</span>
              </div>
              <div className="flex justify-between items-center mb-4 text-gray-600">
                <span>Livraison ({order.shipping_type === 'pickup' ? 'Retrait' : 'Standard'})</span>
                <span>{Number(order.shipping_fee).toLocaleString('fr-FR')} {deviseCommande(order)}</span>
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-xl font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-blue-600">{Number(order.total_amount).toLocaleString('fr-FR')} {deviseCommande(order)}</span>
              </div>
            </div>
          </div>

        </div>
        
        {/* Bouton Donner mon avis - Visible uniquement si commande livrée/completed et pas de feedback */}
        {FEEDBACK_ALLOWED_STATUSES.includes(order.status) && !feedbackExists && (
          <div className="mt-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-center">
            <h3 className="text-white font-semibold text-lg mb-2">
              Votre avis nous intéresse !
            </h3>
            <p className="text-blue-100 text-sm mb-4">
              Partagez votre expérience et aidez-nous à nous améliorer
            </p>
            <Link
              to={`/feedback/${order.id}`}
              className="inline-flex items-center gap-2 bg-white text-blue-600 font-semibold px-6 py-3 rounded-lg shadow hover:bg-blue-50 transition-colors"
            >
              <StarIcon className="h-5 w-5" />
              Donner mon avis
            </Link>
          </div>
        )}

        {/* Message si avis déjà donné */}
        {FEEDBACK_ALLOWED_STATUSES.includes(order.status) && feedbackExists && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-green-700">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="font-medium">Merci pour votre avis !</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Merci de votre confiance !</p>
          <p className="mt-1">Besoin d'aide ? Contactez-nous.</p>
        </div>
      </div>
    </div>
  );
};

export default PublicOrderView;

